import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Coins, Plus, Zap, CreditCard, Wallet } from "lucide-react";
import apiClient from "@/api/ApiClient";
import { isDebug } from "@/config/env";
import { StripeCheckout } from "@/components/StripeCheckout";
import { useCapacitorPlatform } from "@/hooks/use-capacitor";
import type { TokenPackage } from "@/api/models";

interface TokenBalanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userTokens: number;
  onPurchaseTokens: (tokens: number, paymentMethod: string) => void;
  startAtPayment?: boolean; // if true, open directly payment method step
}

export const TokenBalanceModal = ({
  open,
  onOpenChange,
  userTokens,
  onPurchaseTokens,
  startAtPayment
}: TokenBalanceModalProps) => {
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [tokenPackages, setTokenPackages] = useState<TokenPackage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const capacitorPlatform = useCapacitorPlatform();
  const isNative = capacitorPlatform === 'ios' || capacitorPlatform === 'android';

  useEffect(() => {
    const fetchPackages = async () => {
      if (!open) return;
      setIsLoading(true);
      setError(null);
      try {
        const res = await apiClient.getTokenPackages();
        console.log('API response:', res);
        // API vrací pole přímo, ne objekt s items
        const items = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);
        console.log('Token packages items:', items);
        
        if (items.length > 0) {
          setTokenPackages(items);
          const popular = items.find(p => p.popular);
          setSelectedPackageId((popular || items[Math.floor(items.length / 2)]).id);
        } else {
          setError('No token packages available');
        }
      } catch (e: any) {
        console.error('Failed to load token packages from API:', e);
        setError('Failed to load token packages. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPackages();
  }, [open]);

  // If requested, jump straight to checkout when opened
  useEffect(() => {
    if (open && startAtPayment && selectedPackageId) {
      handleStartCheckout();
    }
  }, [open, startAtPayment, selectedPackageId]);
  
  const handleStartCheckout = async () => {
    if (!selectedPackageId) return;

    // In debug mode, use direct token purchase (bypass payment providers)
    if (isDebug) {
      const selectedPkg = tokenPackages.find(p => p.id === selectedPackageId);
      if (selectedPkg) {
        onPurchaseTokens(selectedPkg.tokens, 'card');
      }
      onOpenChange(false);
      return;
    }

    // Check if running in Capacitor (native mobile app)
    if (isNative) {
      // TODO: Implement RevenueCat purchase flow for mobile
      // For now, show message that mobile payments are coming soon
      console.log('Mobile payment via RevenueCat - to be implemented');
      alert('Mobile payments via RevenueCat will be available soon. Please use web version for now.');
      return;
    }

    // Use Stripe Checkout for web
    setShowStripeCheckout(true);
  };

  const resetModals = () => {
    setShowStripeCheckout(false);
  };

  const handleClose = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      resetModals();
    }
  };

  return (
    <>
      {/* Main Token Balance Modal */}
      <Dialog open={open && !showStripeCheckout} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <Coins className="h-6 w-6 text-amber-500" />
              Token Balance
            </DialogTitle>
            <DialogDescription className="text-center">
              Manage your tokens and purchase more to unlock exclusive content
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current Balance */}
            <Card className="p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/20">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Wallet className="h-5 w-5 text-amber-500" />
                  <span className="text-lg font-semibold">Current Balance</span>
                </div>
                <div className="text-3xl font-bold text-amber-600">{userTokens}</div>
                <div className="text-sm text-muted-foreground">tokens available</div>
              </div>
            </Card>

            {/* Purchase Options */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-center">Purchase More Tokens</h3>
              {isLoading && (
                <div className="text-center text-sm text-muted-foreground">Loading packages...</div>
              )}
              {error && (
                <div className="text-center text-sm text-red-600">{error}</div>
              )}
              {!isLoading && !error && tokenPackages.length === 0 && (
                <div className="text-center text-sm text-muted-foreground">No token packages available</div>
              )}
              <div className="grid gap-3">
                {tokenPackages.map((pkg) => (
                  <Card 
                    key={pkg.id}
                    className={`p-4 cursor-pointer transition-all border-2 relative ${
                      selectedPackageId === pkg.id 
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/20' 
                        : 'border-primary/20 hover:border-primary/40'
                    }`}
                    onClick={() => setSelectedPackageId(pkg.id)}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          POPULAR
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <Coins className="h-5 w-5 text-amber-500" />
                        </div>
                        <div>
                          <div className="font-semibold">{pkg.tokens} Tokens</div>
                          <div className="text-sm text-muted-foreground">
                            ${(pkg.price / pkg.tokens * 100).toFixed(1)} per 100 tokens
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-primary">${pkg.price}</div>
                        {selectedPackageId === pkg.id && (
                          <div className="text-xs text-primary font-medium">✓ Selected</div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Purchase Button */}
            <div className="space-y-3">
              <Button 
                variant="romantic" 
                className="w-full"
                onClick={handleStartCheckout}
                disabled={!selectedPackageId}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Purchase {tokenPackages.find(p => p.id === selectedPackageId)?.tokens || ''} Tokens - ${tokenPackages.find(p => p.id === selectedPackageId)?.price || ''}
              </Button>
              
              <div className="text-xs text-muted-foreground text-center">
                Secure payment via Stripe • Instant delivery
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Stripe Checkout Modal - only for web */}
      {showStripeCheckout && selectedPackageId && !isNative && (
        <Dialog open={showStripeCheckout} onOpenChange={(open) => {
          if (!open) {
            setShowStripeCheckout(false);
            resetModals();
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <StripeCheckout
              packageId={selectedPackageId}
              platform="web"
              onSuccess={() => {
                setShowStripeCheckout(false);
                onOpenChange(false);
                // Refresh tokens after successful payment
                window.dispatchEvent(new CustomEvent('tokens-updated'));
              }}
              onCancel={() => {
                setShowStripeCheckout(false);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

    </>
  );
};